import { useEffect } from "react";
import { Outlet } from "react-router";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import HeaderSection from "../components/Header";
import FooterSection from "../components/Footer";
import HeroPortrait from "../assets/sprites/hero-team-nature.webp";
import DifferenceImage from "../assets/sprites/difference-editorial.webp";
import SelectionImage from "../assets/sprites/selection-editorial.webp";
import AssessmentImage from "../assets/sprites/assessment-presentation.webp";
import CoachingImage from "../assets/sprites/coaching-editorial.webp";
import LeadershipImage from "../assets/sprites/leadership-editorial.webp";

const mailTo = (subject) => `mailto:info@sodevaconsulting.com?subject=${encodeURIComponent(subject)}`;

function DifferenceSection({ t }) {
  return (
    <section id="difference" data-reveal className="scroll-mt-24 bg-white px-5 py-16 text-[#111111] sm:px-10 sm:py-20 lg:scroll-mt-28 lg:px-16 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-12 lg:grid-cols-[1.02fr_0.98fr] lg:items-end">
          <div className="border-t border-[#111111] pt-8">
            <div className="flex items-start justify-between gap-8">
              <p className="text-xs font-semibold uppercase text-[#6f5631]">{t("home.difference.kicker")}</p>
              <p className="text-4xl leading-none text-[#B08D57] sm:text-5xl">01</p>
            </div>
            <h2 className="mt-12 max-w-5xl text-4xl font-semibold leading-none sm:mt-16 sm:text-5xl lg:mt-20 lg:text-6xl">{t("home.difference.title")}</h2>
            <p className="mt-6 max-w-3xl text-xl leading-tight text-[#222222] sm:mt-8 sm:text-2xl lg:text-3xl">{t("home.difference.subtitle")}</p>
            <p className="mt-10 max-w-2xl border-l-2 border-[#B08D57] pl-6 text-lg leading-8 text-[#555555]">{t("home.difference.body")}</p>
          </div>

          <div className="relative min-h-[380px] overflow-hidden bg-[#111111] sm:min-h-[460px] lg:min-h-[560px]">
            <img className="cinematic-image absolute inset-0 h-full w-full object-right object-cover grayscale" src={DifferenceImage} alt={t("home.difference.image_alt")} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#11111133] to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 bg-[#111111]/92 p-5 text-white sm:p-8">
              {t("home.difference.proof", { returnObjects: true }).map((item) => (
                <p key={item} className="border-b border-white/20 py-4 text-lg font-semibold leading-tight last:border-b-0 sm:py-5 sm:text-2xl">
                  {item}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SelectionSection({ t }) {
  const proof = t("home.selection.proof", { returnObjects: true });
  const values = t("home.selection.values", { returnObjects: true });

  return (
    <section id="selection" data-reveal className="scroll-mt-24 overflow-hidden bg-[#F5F5F5] px-5 py-16 text-[#111111] sm:px-10 sm:py-20 lg:scroll-mt-28 lg:px-16 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-12 lg:grid-cols-[0.92fr_1.08fr] lg:gap-20">
          <div className="border-t border-[#111111] pt-8">
            <p className="text-xs font-semibold uppercase text-[#6f5631]">{t("home.selection.kicker")}</p>
            <p className="mt-12 text-4xl leading-none text-[#B08D57] sm:mt-16 sm:text-5xl lg:mt-20">02</p>
            <h2 className="mt-10 max-w-xl text-4xl font-semibold leading-none sm:mt-12 sm:text-5xl lg:text-6xl">{t("home.selection.title")}</h2>
            <p className="mt-6 text-xl leading-tight text-[#222222] sm:mt-8 sm:text-2xl lg:text-3xl">{t("home.selection.subtitle")}</p>
          </div>

          <div className="grid gap-8">
            <figure className="relative min-h-[360px] overflow-hidden bg-[#111111] sm:min-h-[440px] lg:min-h-[520px]">
              <img className="cinematic-image absolute inset-0 object-right h-full w-full object-cover grayscale" src={SelectionImage} alt={t("home.selection.image_alt")} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#111111d9] via-transparent to-transparent" />
              <figcaption className="absolute bottom-0 left-0 max-w-2xl p-5 text-xl font-semibold leading-tight text-white sm:p-8 sm:text-2xl lg:text-3xl">
                {t("home.selection.caption")}
              </figcaption>
            </figure>

            <div className="hidden gap-0 border border-[#111111] bg-white md:grid md:grid-cols-2">
              <div className="p-8 md:p-10">
                <p className="text-xs font-semibold uppercase text-[#6f5631]">{t("home.selection.avoid_label")}</p>
                <ul className="mt-10 space-y-6 text-2xl font-semibold leading-tight">
                  {proof.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#111111] p-8 text-white md:p-10">
                <p className="text-xs font-semibold uppercase text-[#B08D57]">{t("home.selection.value_label")}</p>
                <ul className="mt-10 space-y-6 text-2xl leading-tight text-white/80">
                  {values.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AssessmentSection({ t }) {
  const markers = t("home.assessment.markers", { returnObjects: true });

  return (
    <section id="assessment" data-reveal className="scroll-mt-24 bg-[#111111] px-5 py-16 text-white sm:px-10 sm:py-20 lg:scroll-mt-28 lg:px-16 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <figure className="relative min-h-[360px] overflow-hidden sm:min-h-[480px] lg:min-h-[620px]">
            <img className="cinematic-image absolute inset-0 h-full w-full object-cover grayscale" src={AssessmentImage} alt={t("home.assessment.image_alt")} />
            <div className="absolute inset-0 bg-[#111111]/18" />
          </figure>

          <div className="border-t border-white/20 py-10">

            <div className="flex items-start justify-between gap-8">
              <p className="text-xs font-semibold uppercase text-[#B08D57]">{t("home.assessment.kicker")}</p>
              <p className="text-4xl leading-none text-white/85 sm:text-5xl">03</p>
            </div>
            <h2 className="mt-10 max-w-4xl text-4xl font-semibold leading-none sm:mt-12 sm:text-5xl lg:text-6xl">{t("home.assessment.title")}</h2>
            <p className="mt-6 max-w-3xl text-xl leading-tight text-[#f5f5f5] sm:mt-8 sm:text-2xl lg:text-3xl">{t("home.assessment.subtitle")}</p>
            <div className="mt-10 grid sm:mt-14">
              {markers.map((marker) => (
                <p key={marker} className="border-t border-white/20 py-4 text-xl font-semibold leading-tight text-white last:border-b last:border-white/20 sm:py-5 sm:text-2xl lg:text-3xl">
                  {marker}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CoachingSection({ t }) {
  return (
    <section id="coaching" data-reveal className="scroll-mt-24 bg-white px-5 py-16 text-[#111111] sm:px-10 sm:py-20 lg:scroll-mt-28 lg:px-16 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-12 lg:grid-cols-[0.92fr_1.08fr] lg:items-center">
          <div className="border-t border-[#111111] pt-8">
            <div className="flex items-start justify-between gap-8">
              <p className="text-xs font-semibold uppercase text-[#6f5631]">{t("home.coaching.kicker")}</p>
              <p className="text-4xl leading-none text-[#B08D57] sm:text-5xl">04</p>
            </div>
            <h2 className="mt-12 max-w-4xl text-4xl font-semibold leading-none sm:mt-16 sm:text-5xl lg:mt-20 lg:text-6xl">{t("home.coaching.title")}</h2>
            <p className="mt-6 max-w-3xl text-xl leading-tight text-[#222222] sm:mt-8 sm:text-2xl lg:text-3xl">{t("home.coaching.subtitle")}</p>
            <p className="mt-10 max-w-2xl border-l-2 border-[#B08D57] pl-6 text-lg leading-8 text-[#555555]">{t("home.coaching.body")}</p>
          </div>

          <div className="grid gap-8">
            <figure className="relative min-h-[360px] overflow-hidden bg-[#111111] sm:min-h-[460px] lg:min-h-[560px]">
              <img className="cinematic-image object-left absolute inset-0 h-full w-full object-cover grayscale" src={CoachingImage} alt={t("home.coaching.image_alt")} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#11111199] to-transparent" />
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}

function LeadershipSection({ t }) {
  return (
    <section id="leadership" data-reveal className="scroll-mt-24 overflow-hidden bg-[#111111] px-5 py-16 text-white sm:px-10 sm:py-20 lg:scroll-mt-28 lg:px-16 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="grid overflow-hidden border-y border-white/20 bg-[#111111] lg:grid-cols-[0.95fr_1.05fr]">
          <div className="relative z-10 bg-[#111111] px-0 py-8 sm:py-10 lg:px-10 lg:py-14">
            <div className="flex items-start justify-between gap-8">
              <p className="text-xs font-semibold uppercase text-[#B08D57]">{t("home.leadership.kicker")}</p>
              <p className="text-4xl leading-none text-white/85 sm:text-5xl">05</p>
            </div>
            <p className="mt-14 text-6xl font-semibold leading-none text-[#B08D57] brightness-125 sm:mt-20 sm:text-7xl lg:mt-24 lg:text-8xl">1981</p>
            <h2 className="mt-8 max-w-4xl text-4xl font-semibold leading-none text-white sm:mt-10 sm:text-5xl lg:text-6xl">{t("home.leadership.title")}</h2>
            <p className="mt-8 max-w-2xl border-l-2 border-[#B08D57] pl-5 text-xl leading-snug text-white sm:mt-10 sm:pl-6 sm:text-2xl">{t("home.leadership.body")}</p>
          </div>
          <div className="relative min-h-[360px] overflow-hidden bg-[#111111] sm:min-h-[480px] lg:min-h-[580px]">
            <img className="cinematic-image absolute inset-0 h-full w-full object-cover grayscale" src={LeadershipImage} alt={t("home.leadership.image_alt")} />
            <div className="absolute inset-0 bg-gradient-to-r from-[#11111166] via-transparent to-[#1111111a]" />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const revealTargets = document.querySelectorAll("[data-reveal]");

    if (!("IntersectionObserver" in window)) {
      revealTargets.forEach((target) => target.classList.add("is-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.14 }
    );

    revealTargets.forEach((target) => observer.observe(target));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!location.hash) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      const target = document.getElementById(location.hash.slice(1));
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 80);

    return () => window.clearTimeout(timeout);
  }, [location.hash]);

  return (
    <div className="bg-white text-[#111111]">
      <HeaderSection />
      <main>
        <section id="top" className="relative flex min-h-screen items-center overflow-hidden bg-[#111111] px-5 pb-20 pt-40 text-white sm:px-10 sm:py-32 lg:px-16">
          <img
            className="cinematic-image absolute inset-0 h-full w-full object-cover grayscale"
            src={HeroPortrait}
            alt={t("home.hero.image_alt")}
          />
          <div className="absolute inset-0 bg-[#111111]/32" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#111111] via-[#111111d9] to-[#1111110d]" />
          <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[#111111] to-transparent" />
          <div className="absolute bottom-12 left-6 top-32 hidden w-px bg-gradient-to-b from-transparent via-[#B08D57] to-transparent opacity-70 lg:block" />

          <div className="relative z-10 mx-auto w-full max-w-7xl">
            <div className="max-w-4xl">
              <h1 className="mt-8 max-w-5xl text-5xl font-semibold leading-none sm:text-6xl md:text-7xl lg:text-[5.5rem]">
                {t("home.hero.title")}
              </h1>
              <p className="mt-6 text-xl leading-tight text-[#f5f5f5] sm:mt-8 sm:text-2xl lg:text-3xl">{t("home.hero.subtitle")}</p>
              <div className="mt-8 flex max-w-3xl flex-col gap-6 border-l-2 border-[#B08D57] pl-5 sm:mt-10 sm:gap-8 sm:pl-6">
                <p className="max-w-xl text-lg leading-8 text-[#e5e5e5]">{t("home.hero.body")}</p>
                <a
                  className="inline-flex min-h-14 w-fit max-w-full items-center border border-[#B08D57] bg-[#B08D57] px-5 py-4 text-sm font-semibold uppercase text-[#111111] transition duration-200 hover:bg-white hover:text-[#111111] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white sm:px-7"
                  href={mailTo(t("home.cta_subject"))}
                >
                  {t("home.hero.cta")}
                </a>
              </div>
            </div>
          </div>
        </section>

        <DifferenceSection t={t} />
        <SelectionSection t={t} />
        <AssessmentSection t={t} />
        <CoachingSection t={t} />
        <LeadershipSection t={t} />
      </main>
      <FooterSection />
      <Outlet />
    </div>
  );
}
